
// src/pages/Dashboard/Sidebar.jsx
import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './Sidebar.module.css';

const Sidebar = ({ isOpen, onClose, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');

  // Define all menu items (icons removed)
  const allMenuItems = [
    {
      section: 'Main',
      items: [
        { label: 'Dashboard', path: '/dashboard', roles: [1, 2, 3] }
      ]
    },
    {
      section: 'Orders',
      items: [
        { label: 'All Orders(Line Items)', path: '/dashboard/orders/all', roles: [1, 2, 3] },
        { label: 'Header Order', path: '/dashboard/orders/header', roles: [1, 2, 3] }
      ]
    },
    {
      section: 'Invoice',
      items: [
        { label: 'All Invoice(Line Items)', path: '/dashboard/invoice/all', roles: [1, 2, 3] },
        { label: 'Pending for Dispatch', path: '/dashboard/invoice/pending', roles: [1, 2, 3] },
        { label: 'Header Invoice', path: '/dashboard/invoice/header', roles: [1, 2, 3] }
      ]
    },
    {
      section: 'Management',
      items: [
        { label: 'Customer', path: '/dashboard/customer', roles: [2, 3] },
        { label: 'Products', path: '/dashboard/products', roles: [2, 3] }
      ]
    },
    {
      section: 'Financial',
      items: [
        { label: 'Customer Balance', path: '/dashboard/balance', roles: [2, 3] },
        { label: 'Vendor Balance', path: '/dashboard/vendor-balance', roles: [2, 3] },
        { label: 'Customer Balance', path: '/dashboard/customerbalance', roles: [1] },
        { label: 'Target Analytics', path: '/dashboard/target-analytics', roles: [2, 3] },
        { label: 'Category Analytics', path: '/dashboard/category-analytics', roles: [2, 3] }
      ]
    },
    {
      section: 'Operations',
      items: [
        { label: 'Quick Quote', path: '/dashboard/quick-quote', roles: [1, 2, 3] },
        // { label: 'Order LifeCycle', path: '/dashboard/order-lifecycle', roles: [2, 3] },
        // { label: 'In Bound Shipments', path: '/dashboard/inbound-shipments', roles: [2, 3] },
        { label: 'Document Downloading', path: '/dashboard/documents', roles: [2, 3] },
        // { label: 'Dispatch Details', path: '/dashboard/dispatch-details', roles: [2, 3] }
      ]
    }
  ];

  // Filter menu items based on user role
  const roleBasedMenuItems = useMemo(() => {
    if (!user || !user.roleId) return [];

    const userRoleId = user.roleId;

    return allMenuItems
      .map(section => ({
        ...section,
        items: section.items.filter(item => 
          item.roles && item.roles.includes(userRoleId)
        )
      }))
      .filter(section => section.items.length > 0);
  }, [user]);

  // Apply search filter on top of role filter
  const filteredMenuItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return roleBasedMenuItems;
    }

    const query = searchQuery.toLowerCase();
    return roleBasedMenuItems
      .map(section => ({
        ...section,
        items: section.items.filter(item =>
          item.label.toLowerCase().includes(query) ||
          section.section.toLowerCase().includes(query)
        )
      }))
      .filter(section => section.items.length > 0);
  }, [searchQuery, roleBasedMenuItems]);

  const handleMenuClick = (path) => {
    navigate(path);
    if (window.innerWidth <= 768) {
      onClose();
    }
  };

  return (
    <>
      <div 
        className={`${styles.overlay} ${!isOpen ? styles.overlayHidden : ''}`}
        onClick={onClose}
      />
      <aside className={`${styles.sidebarContainer} ${!isOpen ? styles.sidebarClosed : ''}`}>
        <div className={styles.searchContainer}>
          <div className={styles.searchInputWrapper}>
            <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M7 13C10.3137 13 13 10.3137 13 7C13 3.68629 10.3137 1 7 1C3.68629 1 1 3.68629 1 7C1 10.3137 3.68629 13 7 13Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M11.5 11.5L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              placeholder="Search menu..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>
        
        <div className={styles.sidebarContent}>
          {filteredMenuItems.length > 0 ? (
            filteredMenuItems.map((section, idx) => (
              <div key={idx} className={styles.menuSection}>
                <h3 className={styles.menuTitle}>{section.section}</h3>
                {section.items.map((item, itemIdx) => {
                  const isActive = location.pathname === item.path;
                  const isSubItem = item.subItem;
                  
                  return (
                    <button
                      key={itemIdx}
                      className={
                        isSubItem
                          ? `${styles.subMenuItem} ${isActive ? styles.subMenuItemActive : ''}`
                          : `${styles.menuItem} ${isActive ? styles.menuItemActive : ''}`
                      }
                      onClick={() => handleMenuClick(item.path)}
                    >
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            ))
          ) : (
            <div className={styles.noResults}>No menu items found</div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;